
import { PipelineDetail } from '@/actions/data-fetch-pipelines';
import {
  Edit,
  Activity,
  FileText,
  ChevronRight
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface PipelineListViewProps {
  initialData: PipelineDetail[];
  onEdit: (pipeline: PipelineDetail) => void;
  pagination?: PaginationProps;
  onPageChange: (page: number) => void;
  onSearch: (term: string) => void;
  isLoading: boolean;
  searchTerm: string;
}

const PipelineListView: React.FC<PipelineListViewProps> = ({
  initialData,
  onEdit,
  pagination,
  onPageChange,
  // @ts-expect-error - Reserved for future search functionality
  onSearch,
  isLoading,
  // @ts-expect-error - Reserved for future search functionality
  searchTerm
}) => {

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const getStatusFlow = (statusOptions: any[]) => {
    if (!statusOptions || statusOptions.length === 0) return [];
    
    return statusOptions
      .sort((a, b) => a.opt_order - b.opt_order)
      .map(status => status.opt_name);
  };

  return (
    <div className="space-y-6">
      {/* Pipeline List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12">
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600"></div>
              <span className="text-gray-500 dark:text-gray-400">Loading pipelines...</span>
            </div>
          </div>
        ) : initialData.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12">
            <div className="text-center">
              <Activity className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No pipelines found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Create your first pipeline to get started with organizing your sales process.
              </p>
            </div>
          </div>
        ) : (
          initialData.map((pipeline) => {
            const statusFlow = getStatusFlow(pipeline.status_options || []);
            
            return (
              <div
                key={pipeline.uuid}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
              >
                {/* Pipeline Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-lg bg-brand-100 dark:bg-brand-900/20 flex items-center justify-center">
                          <FileText className="h-6 w-6 text-brand-600 dark:text-brand-400" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {pipeline.pipeline_name}
                        </h3>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <span>Created {formatDate(pipeline.created_at)}</span>
                          <span>•</span>
                          <span>{statusFlow.length} stages</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                        Active
                      </span>
                      <button
                        onClick={() => onEdit(pipeline)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </button>
                    </div>
                  </div>
                </div>

                {/* Status Flow */}
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <Activity className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Status Flow
                    </span>
                  </div>
                  
                  {statusFlow.length > 0 ? (
                    <div className="flex items-center flex-wrap gap-2">
                      {statusFlow.map((status, index) => (
                        <div key={index} className="contents">
                          <div className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border">
                            {status}
                          </div>
                          {index < statusFlow.length - 1 && (
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                      No status flow configured
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to {' '}
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {' '}
              {pagination.totalItems} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPreviousPage}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const page = i + Math.max(1, pagination.currentPage - 2);
                if (page > pagination.totalPages) return null;
                
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${
                      page === pagination.currentPage
                        ? 'bg-brand-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineListView;